import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { triggerXPToast } from "@/components/leaderboard/XPToast";

interface UserProgress {
  id: string;
  user_id: string;
  task_id: string;
  is_correct: boolean;
  attempts: number;
  last_attempt_at: string;
  created_at: string;
}

interface UserStats {
  id: string;
  user_id: string;
  total_answered: number;
  total_correct: number;
  current_streak: number;
  best_streak: number;
  last_activity_at: string;
  updated_at: string;
}

export const userProgressKeys = {
  all: ["userProgress"] as const,
  progress: () => [...userProgressKeys.all, "progress"] as const,
  stats: () => [...userProgressKeys.all, "stats"] as const,
  taskProgress: (taskId: string) => [...userProgressKeys.progress(), taskId] as const,
};

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const GC_TIME = 30 * 60 * 1000; // 30 minutes

export function useUserProgressQuery() {
  const { user } = useAuth();

  return useQuery({
    queryKey: userProgressKeys.progress(),
    queryFn: async (): Promise<UserProgress[]> => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("user_progress")
        .select("*")
        .order("last_attempt_at", { ascending: false });

      if (error) {
        console.error("Error fetching user progress:", error);
        throw error;
      }

      return data || [];
    },
    enabled: !!user,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useUserStatsQuery() {
  const { user } = useAuth();

  return useQuery({
    queryKey: userProgressKeys.stats(),
    queryFn: async (): Promise<UserStats | null> => {
      if (!user) return null;

      const { data, error } = await supabase
        .from("user_stats")
        .select("*")
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          // No stats yet - return null
          return null;
        }
        console.error("Error fetching user stats:", error);
        throw error;
      }

      return data;
    },
    enabled: !!user,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRecordAttemptMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      taskId,
      isCorrect,
    }: {
      taskId: string;
      isCorrect: boolean;
    }) => {
      if (!user) {
        // Not logged in - skip database save
        return null;
      }

      const { error } = await supabase.rpc("record_task_attempt", {
        p_task_id: taskId,
        p_is_correct: isCorrect,
      });

      if (error) {
        console.error("Error recording attempt:", error);
        throw error;
      }

      return { taskId, isCorrect };
    },
    onSuccess: (result) => {
      // Invalidate both progress and stats queries
      queryClient.invalidateQueries({ queryKey: userProgressKeys.progress() });
      queryClient.invalidateQueries({ queryKey: userProgressKeys.stats() });
      queryClient.invalidateQueries({ queryKey: ["user-xp"] });
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      
      // Show XP toast for logged-in users
      if (result) {
        const baseXP = result.isCorrect ? 10 : 2;
        triggerXPToast(baseXP, result.isCorrect ? "za poprawną odpowiedź" : "za próbę");
      }
    },
  });
}

export function useTaskProgressQuery(taskId: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: userProgressKeys.taskProgress(taskId || ""),
    queryFn: async (): Promise<UserProgress | null> => {
      if (!user || !taskId) return null;

      const { data, error } = await supabase
        .from("user_progress")
        .select("*")
        .eq("task_id", taskId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching task progress:", error);
        throw error;
      }

      return data;
    },
    enabled: !!user && !!taskId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// Hook to sync local storage with database on login
export function useSyncProgressOnLogin() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const syncMutation = useMutation({
    mutationFn: async (localHistory: Array<{ taskId: string; isCorrect: boolean }>) => {
      if (!user || localHistory.length === 0) return;

      // Batch sync - for each local entry, record attempt
      for (const entry of localHistory) {
        try {
          await supabase.rpc("record_task_attempt", {
            p_task_id: entry.taskId,
            p_is_correct: entry.isCorrect,
          });
        } catch (error) {
          console.error("Error syncing entry:", error);
          // Continue with other entries
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userProgressKeys.all });
    },
  });

  return syncMutation;
}
